<template>
  <div v-resize="onResize" ref="container" class="caption">
    <AppChartLegend :items="legendItems" />
    <svg :width="chartWidth" :height="chartHeight">
      <g :transform="`translate(${margin.left},${margin.top})`">
        <g :transform="`translate(0,${height})`" text-anchor="middle">
          <g
            v-for="tick in xTicks"
            :key="tick"
            :transform="`translate(${x(tick)},0)`"
          >
            <line
              :y1="-height"
              :stroke="$vuetify.theme.themes.light.secondary"
            ></line>
            <text :y="tickPadding" dy="0.68em">{{ formatTick(tick) }}</text>
          </g>
          <line
            :x1="x(useThreshold)"
            :x2="x(useThreshold)"
            :y1="-height"
            stroke="#000"
            stroke-dasharray="4"
          ></line>
          <text
            :x="x(useThreshold)"
            :y="-height - tickPadding"
            text-anchor="middle"
          >
            {{ formatTick(useThreshold) }}
          </text>
          <text
            :x="width / 2"
            :y="margin.bottom"
            dy="-1em"
            text-anchor="middle"
            class="subtitle-2"
          >
            {{ $t("helpfulness.use") }}
          </text>
        </g>
        <g
          :transform="$vuetify.rtl ? `translate(${width}, 0)` : ''"
          text-anchor="end"
        >
          <g
            v-for="tick in yTicks"
            :key="tick"
            :transform="`translate(0,${y(tick)})`"
          >
            <line
              :x1="$vuetify.rtl ? -width : width"
              :stroke="$vuetify.theme.themes.light.secondary"
            ></line>
            <text :x="$vuetify.rtl ? tickPadding : -tickPadding" dy="0.32em">
              {{ formatTick(tick) }}
            </text>
          </g>
          <line
            :y1="y(helpfulnessThreshold)"
            :y2="y(helpfulnessThreshold)"
            :x1="$vuetify.rtl ? -width : width"
            stroke="#000"
            stroke-dasharray="4"
          ></line>
          <text
            :x="$vuetify.rtl ? -width - tickPadding : width + tickPadding"
            :y="y(helpfulnessThreshold)"
            dy="0.35em"
            text-anchor="start"
          >
            {{ formatTick(helpfulnessThreshold) }}
          </text>
          <text
            :y="height / 2"
            :x="$vuetify.rtl ? margin.right : -margin.left"
            :dy="$vuetify.rtl ? '-1em' : '1em'"
            text-anchor="middle"
            :transform="
              $vuetify.rtl
                ? `translate(${margin.right},${height /
                    2})rotate(-90)translate(${-margin.right},${-height / 2})`
                : `translate(${-margin.left},${height /
                    2})rotate(-90)translate(${margin.left},${-height / 2})`
            "
            class="subtitle-2"
          >
            {{ $t("helpfulness.helpfulness") }}
          </text>
        </g>
        <g>
          <line
            v-for="(label, i) in labelArray"
            :key="label.name"
            :x1="anchorArray[i].x"
            :y1="anchorArray[i].y"
            :x2="$vuetify.rtl ? label.x - symbolSize : label.x"
            :y2="label.y"
            stroke="#000"
            stroke-width="0.5"
          ></line>
        </g>
        <g>
          <g
            v-for="d in percentageData"
            :key="d.key"
            :transform="`translate(${x(d.x)},${y(d.y)})`"
          >
            <rect
              v-if="d.shape === 'square'"
              :x="-symbolSize * 0.45"
              :y="-symbolSize * 0.45"
              :width="symbolSize * 0.9"
              :height="symbolSize * 0.9"
              :fill="d.color"
            ></rect>
            <circle
              v-else-if="d.shape === 'circle'"
              :r="symbolSize / 2"
              :fill="d.color"
            ></circle>
            <polygon
              v-else-if="d.shape === 'triangle'"
              :points="
                `-${symbolSize / 2},${symbolSize / 2} ${symbolSize /
                  2},${symbolSize / 2} 0,${-symbolSize / 2}`
              "
              :fill="d.color"
            ></polygon>
          </g>
          <g>
            <text
              v-for="label in labelArray"
              :key="label.name"
              :x="$vuetify.rtl ? label.x - symbolSize : label.x"
              :y="label.y"
              dy="0.32em"
            >
              {{ label.name }}
            </text>
          </g>
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
import AppChartLegend from "@/components/AppChartLegend.vue";
import { mapGetters } from "vuex";
import filtersInfo from "@/locales/en.json";
import { quantize, interpolateHcl } from "d3-interpolate";
import { scaleOrdinal, scaleLinear } from "d3-scale";
import { format } from "d3-format";
import d3Labeler from "@/utils/d3Labeler";
export default {
  components: {
    AppChartLegend
  },
  data() {
    return {
      chartWidth: 600,
      chartHeight: 600,
      tickPadding: 12,
      symbolSize: 16,
      helpfulnessThreshold: 0.5,
      useThreshold: 0.2,
      labelHeight: 18,
      labelCharWidth: 6.5,
      strategies: Object.keys(
        filtersInfo.filters.resolutions.resolutionStrategy.options
      )
    };
  },
  computed: {
    ...mapGetters([
      "filteredCount",
      "filteredData",
      "filteredScatterHelpfulnessData"
    ]),
    filteredScatterHelpfulness() {
      return this.filteredScatterHelpfulnessData();
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    margin() {
      return this.$vuetify.rtl
        ? { top: 24, right: 60, bottom: 48, left: 36 }
        : { top: 24, right: 36, bottom: 48, left: 60 };
    },
    width() {
      return this.chartWidth - this.margin.left - this.margin.right;
    },
    height() {
      return this.chartHeight - this.margin.top - this.margin.bottom;
    },
    colorScale() {
      const strategies = this.strategies;
      const unknown = this.$vuetify.theme.themes.light.secondary;
      const colors = ["#194173", "#A7C7AD", "#5B2A7E"];
      return scaleOrdinal()
        .domain(strategies)
        .range(colors);
    },
    shapeScale() {
      const strategies = this.strategies;
      return scaleOrdinal()
        .domain(strategies)
        .range(["square", "circle", "triangle"]);
    },
    x() {
      return scaleLinear()
        .domain([0, 1])
        .range(this.$vuetify.rtl ? [this.width, 0] : [0, this.width]);
    },
    y() {
      return scaleLinear()
        .domain([0, 1])
        .range([this.height, 0]);
    },
    xTicks() {
      const n = Math.floor(this.width / 80);
      return this.x.ticks(n);
    },
    yTicks() {
      const n = Math.floor(this.height / 80);
      return this.y.ticks(n);
    },
    formatTick() {
      return format(".0%");
    },
    percentageData() {
      return this.filteredScatterHelpfulness.map(d => {
        const value = d.value;
        const [key1, key2] = d.key.split("-");
        return {
          key: d.key,
          label: this.$t(`strategies.${key1}.paths.${key2}`),
          x: value[0],
          y: value[1],
          color: this.colorScale(key1),
          shape: this.shapeScale(key1)
        };
      });
    },
    labelArray() {
      return this.percentageData.map(d => ({
        x: this.x(d.x),
        y: this.y(d.y),
        width: this.labelCharWidth * d.label.length,
        height: this.labelHeight,
        name: d.label
      }));
    },
    anchorArray() {
      return this.percentageData.map(d => ({
        x: this.x(d.x),
        y: this.y(d.y),
        r: this.symbolSize / 2
      }));
    },
    labeler() {
      return d3Labeler()
        .label(this.labelArray)
        .anchor(this.anchorArray)
        .width(this.width)
        .height(this.height);
    },
    legendItems() {
      return this.strategies.map(key => ({
        label: this.$t(`filters.resolutions.resolutionStrategy.options.${key}`),
        color: this.colorScale(key),
        shape: this.shapeScale(key)
      }));
    }
  },
  watch: {
    breakpoint() {
      this.onResize();
    },
    labeler() {
      this.labeler.start(100);
    }
  },
  methods: {
    onResize() {
      this.chartWidth = this.$refs.container.clientWidth;
    }
  },
  mounted() {
    this.labeler.start(100);
  }
};
</script>

<style scoped>
</style>